/**
 * Delivery Lead-Time Manager  (V3.1 / 2026-10-05)
 * ------------------------------------------------------------------
 * 지정일 배송 상품이 카트에 있으면 캘린더 날짜를 제한하고,
 * 판매 마감 시각 이후 구매를 막는다. 세 가지 종류를 지원한다.
 *
 * ① `leadtime` 태그 (데일리 드롭) — 그 날짜 하나만. 날짜는 메타오브젝트 limited_drop (사이트 공용 1개)
 *      theme.liquid 가 window.limitedDrop = [{local, frozen}, ...] 로 주입
 *
 * ② `kimchi-preorder` 태그 (김치 사전예약 주문) — 그 날짜 하나만. 날짜는 상품 메타필드 (상품별)
 *      custom.local_delivery_date / custom.frozen_delivery_date
 *      카트 페이지 Liquid 가 window.bcPreorder = [{handle, local, frozen}, ...] 로 주입
 *      상품 페이지 스니펫이 window.bcPreorderProduct = {handle, local, frozen} 로 주입
 *
 * ③ `preorder-from` 태그 (사전예약, V3.1 신규) — limited_drop 날짜 "부터" 고를 수 있음
 *      그 날짜 이전 칩만 숨기고, 그 날짜와 이후 날짜는 그대로 고를 수 있다. 판매 마감 처리 없음.
 *      그 날짜 전날 오전 5시(LA)가 지나면 아무 제한·표시도 하지 않는다 — 그때부터는 배송 캘린더 마감 규칙상
 *      그 날짜 이전 날을 어차피 고를 수 없어서 (직접배송 당일 5시 · FedEx 전날 5시 · 픽업 당일/전날 5시).
 *      `leadtime` 태그가 같이 붙어 있어도 `preorder-from` 이 우선 (테마 교체 전후 끊김 없이 쓰려고).
 *      카트 Liquid 가 leadtime 태그를 보고 숨긴 날짜 칸(.bc-hide-when-limited)은 다시 보이게 한다.
 *
 *   - 로컬 손님(LA_OC)  → local 날짜 / 냉동 손님(SOCAL/NATIONWIDE/미등록) → frozen 날짜
 *   - ①② 가 섞이면 로컬 배송일이 더 늦은 쪽으로 잠금 (주문 전체가 함께 나감)
 *   - ①② 가 있으면 ③ 보다 우선 (그날 하나만)
 *   - ①② 판매 마감 = 로컬 배송일 오전 5시(LA). 이후 해당 상품 '주문 마감' 처리.
 *
 * V3.1 변경점
 *   - ③ preorder-from 지원
 *   - 장바구니 줄·미니카트 줄마다 어떤 상품이 한정/사전예약인지 표시 (.dlm-item-flag)
 *   - 캘린더 위 안내에 상품 이름, 한국어/영어
 *   - 상품 페이지 pn365 안내를 preorder-from 상품에 맞게 바꿈
 *
 * 의존: window.ChefBanchan.ZipCodeManager (zone), window.limitedDrop, window.bcPreorder
 * 안전: reload/카트변경/재고변경 없음 (캘린더 칩 숨김 + 목표일 클릭 + 버튼 비활성 + 표시만).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.DeliveryLeadtimeManager) return; // 중복 로드 방지

  // ============================================================
  // ⚙️ 설정
  // ============================================================
  var CONFIG = {
    tag: 'leadtime',                // 데일리 드롭 태그 (그날 하나만)
    preorderTag: 'kimchi-preorder', // 김치 사전예약 주문 태그 (그날 하나만)
    fromTag: 'preorder-from',       // 사전예약 태그 (그날부터, V3.1)
    localZone: 'LA_OC',             // 로컬로 취급할 zone
    cutoffHour: 5,                  // 로컬 배송일 이 시각(LA, 24h) 이후 판매 마감 (①②)
    showNotice: true,               // 캘린더 위 안내 문구
    showItemFlags: true,            // 장바구니·미니카트 상품 줄 표시
    closedText: 'Order closed — this limited item is no longer available.',
    preorderClosedText: 'Kimchi preorder closed: this item is no longer available.',
    debug: true                     // 콘솔 로그 (테스트 끝나면 false 권장)
  };

  var TXT = {
    ko: {
      lockNotice: '한정 상품({names})이 있어서 {date}에만 배송돼요.',
      preNotice: '김치 사전예약({names})이 있어서 주문 전체가 {date}에 함께 배송돼요.',
      fromNotice: '사전예약 상품({names})은 {date}부터 받을 수 있어요. 그날이나 그 이후 날짜를 골라주세요.',
      lockFlag: '한정 · {date}에만 배송',
      lockFlagNoDate: '한정 · 지정일 배송',
      preFlag: '김치 사전예약 · {date} 배송',
      preFlagNoDate: '김치 사전예약',
      fromFlag: '사전예약 · {date}부터 배송',
      pnTitle: '사전예약',
      pnDate: '{date}부터 받을 수 있어요',
      pnSub: '그 이후 날짜도 고를 수 있어요 · 소진 시 조기 마감',
      fromLine: '사전예약 · {date}부터 배송 · 그 이후 날짜도 선택 가능'
    },
    en: {
      lockNotice: 'Limited item in cart ({names}) — delivers {date} only.',
      preNotice: 'Kimchi preorder in cart ({names}): your whole order will be delivered {date}.',
      fromNotice: 'Pre-order item in cart ({names}) — delivers from {date}. Pick that day or later.',
      lockFlag: 'Limited · {date} only',
      lockFlagNoDate: 'Limited · scheduled date',
      preFlag: 'Kimchi preorder · {date}',
      preFlagNoDate: 'Kimchi preorder',
      fromFlag: 'Pre-order · from {date}',
      pnTitle: 'Pre-order',
      pnDate: 'Delivers from {date}',
      pnSub: 'Later dates available · while supplies last',
      fromLine: 'Pre-order · delivers from {date} · later dates available'
    }
  };

  function log() {
    if (CONFIG.debug && window.console) {
      console.log.apply(console, ['🕒 [Leadtime]'].concat([].slice.call(arguments)));
    }
  }

  function lang() {
    var l = String(document.documentElement.lang || '').toLowerCase();
    return l.indexOf('ko') === 0 ? 'ko' : 'en';
  }

  function T() { return TXT[lang()]; }

  // ============================================================
  // 시간/날짜 — 가게 기준(LA) 시간 고정
  // ============================================================
  function laNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  }

  function parseYmd(s) {
    if (!s) return null;
    var p = String(s).split('-');
    if (p.length < 3) return null;
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  function ymd(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  // '10월 7일(수)' / 'Wed, Oct 7'
  function dateLabel(s) {
    var d = parseYmd(s);
    if (!d) return s || '';
    if (lang() === 'ko') {
      return (d.getMonth() + 1) + '월 ' + d.getDate() + '일(' + ['일', '월', '화', '수', '목', '금', '토'][d.getDay()] + ')';
    }
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] + ', ' +
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()] + ' ' + d.getDate();
  }

  // 템플릿 채우기 — 이름이 없으면 '({names})' 부분을 통째로 뺀다
  function fill(tpl, dateStr, names) {
    var s = String(tpl).replace('{date}', dateStr ? dateLabel(dateStr) : '');
    if (names) return s.replace('{names}', names);
    return s.replace(/\s*\(\{names\}\)/, '').replace('{names}', '');
  }

  // ============================================================
  // 데일리 드롭 — window.limitedDrop 중 로컬 배송일이 가장 늦은(=최신) 항목
  // ============================================================
  function getDrop() {
    var drops = window.limitedDrop;
    if (!drops) return null;
    if (!Array.isArray(drops)) drops = [drops];
    var best = null;
    for (var i = 0; i < drops.length; i++) {
      var d = drops[i];
      if (!d || !d.local) continue;
      if (!best || d.local > best.local) best = d;
    }
    return best ? { local: best.local, frozen: best.frozen || '', kind: 'leadtime' } : null;
  }

  // ============================================================
  // 예약판매 — window.bcPreorder 에서 handle 로 찾기
  // ============================================================
  function getPreorderByHandle(handle) {
    var list = window.bcPreorder;
    if (!list) return null;
    if (!Array.isArray(list)) list = [list];
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (p && p.handle === handle && p.local) return { local: p.local, frozen: p.frozen || '', kind: 'preorder', handle: handle };
    }
    return null;
  }

  // ============================================================
  // zone — localStorage zip → 없으면 장바구니 속성(_order_tags: ZIP_xxxxx)
  // ============================================================
  var cartZip = '';

  function getZone() {
    var zip = '';
    try {
      var zm = window.ChefBanchan && window.ChefBanchan.ZipCodeManager;
      if (zm && typeof zm.getCurrentZipCode === 'function') zip = zm.getCurrentZipCode() || '';
      if (!zip) zip = cartZip;
      if (zip && zm && typeof zm.getDeliveryZone === 'function') return zm.getDeliveryZone(zip);
    } catch (e) { /* noop */ }
    return localStorage.getItem('chefBanchanZone') || 'NATIONWIDE';
  }

  function getZipForCalendar() {
    var zip = '';
    try {
      var zm = window.ChefBanchan && window.ChefBanchan.ZipCodeManager;
      if (zm && typeof zm.getCurrentZipCode === 'function') zip = zm.getCurrentZipCode() || '';
    } catch (e) { /* noop */ }
    return zip || cartZip || '';
  }

  // 이 손님(zone)에게 맞는 날짜 하나 (local / frozen)
  function zoneDate(d) {
    if (!d) return null;
    return (getZone() === CONFIG.localZone) ? (d.local || null) : (d.frozen || d.local || null);
  }

  // ============================================================
  // 상품 태그 → 종류 ('preorder' | 'from' | 'leadtime' | null)
  // ============================================================
  function kindFromTags(tags) {
    if (!tags) return null;
    var t = [];
    for (var i = 0; i < tags.length; i++) t.push(String(tags[i]).toLowerCase().trim());
    if (t.indexOf(CONFIG.preorderTag.toLowerCase()) !== -1) return 'preorder';
    if (t.indexOf(CONFIG.fromTag.toLowerCase()) !== -1) return 'from';
    if (t.indexOf(CONFIG.tag.toLowerCase()) !== -1) return 'leadtime';
    return null;
  }

  var tagCache = {};

  function productKind(handle) {
    if (tagCache.hasOwnProperty(handle)) return Promise.resolve(tagCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var kind = (p && Array.isArray(p.tags)) ? kindFromTags(p.tags) : null;
        tagCache[handle] = kind;
        return kind;
      })
      .catch(function () { tagCache[handle] = null; return null; });
  }

  // 하위호환 (다른 스크립트/콘솔에서 쓰던 API)
  function productHasTag(handle) {
    return productKind(handle).then(function (k) { return k === 'leadtime'; });
  }

  // ============================================================
  // 카트 상태
  // ============================================================
  var cartHasLeadtime = false;
  var cartPreorders = [];     // [{local, frozen, kind:'preorder', handle}]
  var cartFroms = [];         // [{local, frozen, kind:'from', handle}]
  var kindByHandle = {};      // handle → kind (카트 안 상품)
  var titleByHandle = {};     // handle → 상품 이름 (현재 언어)

  // 잠금 드롭 (①②) — 로컬 배송일이 가장 늦은 것
  function getActiveDrop() {
    var cands = [];
    if (cartHasLeadtime) { var d = getDrop(); if (d) cands.push(d); }
    for (var i = 0; i < cartPreorders.length; i++) cands.push(cartPreorders[i]);
    var best = null;
    for (var k = 0; k < cands.length; k++) {
      if (!best || cands[k].local > best.local) best = cands[k];
    }
    return best;
  }

  // 이 손님(zone)에게 허용된 목표 배송일 (①② 잠금). 없으면 null.
  function getTargetStr() {
    var drop = getActiveDrop();
    if (!drop) return null;
    return zoneDate(drop);
  }

  // ③ 사전예약이 아직 의미 있는가 — 시작일 전날 오전 5시(LA) 전까지만
  function fromActive(minStr) {
    var d = parseYmd(minStr);
    if (!d) return false;
    var until = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1, CONFIG.cutoffHour, 0, 0);
    return laNow() < until;
  }

  // ③ 사전예약 — 이 손님이 고를 수 있는 가장 빠른 날 (지났으면 null)
  function getFromMin() {
    var best = null;
    for (var i = 0; i < cartFroms.length; i++) {
      var t = zoneDate(cartFroms[i]);
      if (t && (!best || t > best)) best = t;
    }
    if (!best) return null;
    if (!fromActive(best)) return null; // 지난 사전예약은 제한 없음
    return best;
  }

  // 판매 마감 여부 (①②) — 로컬 배송일 cutoffHour(05시) 이후면 true
  function isClosedFor(drop) {
    if (!drop || !drop.local) return false;
    var d = parseYmd(drop.local);
    if (!d) return false;
    var cutoff = new Date(d.getFullYear(), d.getMonth(), d.getDate(), CONFIG.cutoffHour, 0, 0);
    return laNow() >= cutoff;
  }

  function isClosed() { return isClosedFor(getDrop()); }

  function cartJsUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart.js';
  }

  function namesFor(kind) {
    var out = [];
    for (var h in kindByHandle) {
      if (kindByHandle.hasOwnProperty(h) && kindByHandle[h] === kind) out.push(titleByHandle[h] || h);
    }
    return out.join(', ');
  }

  // ============================================================
  // 장바구니 재평가 → ruleActive (카트에 지정일 상품 있나)
  // ============================================================
  var ruleActive = false;
  var lastFingerprint = null;

  function refresh() {
    return fetch(cartJsUrl(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var attrs = (cart && cart.attributes) || {};
        var zipMatch = /ZIP_(\d{5})/.exec(attrs._order_tags || '');
        if (zipMatch) cartZip = zipMatch[1];

        for (var t = 0; t < items.length; t++) {
          if (items[t] && items[t].handle) titleByHandle[items[t].handle] = items[t].product_title || items[t].title || items[t].handle;
        }

        var fp = cartZip + '::' + items.map(function (i) { return i.handle; }).sort().join('|');
        if (fp === lastFingerprint) { scheduleApply(); scheduleFlags(); return ruleActive; }
        lastFingerprint = fp;

        if (!items.length) {
          ruleActive = false; cartHasLeadtime = false; cartPreorders = []; cartFroms = []; kindByHandle = {};
          scheduleApply(); scheduleFlags();
          return false;
        }

        var uniq = items.map(function (i) { return i.handle; }).filter(Boolean)
          .filter(function (h, idx, a) { return a.indexOf(h) === idx; });
        return Promise.all(uniq.map(productKind)).then(function (kinds) {
          cartHasLeadtime = false;
          cartPreorders = [];
          cartFroms = [];
          kindByHandle = {};
          for (var i = 0; i < kinds.length; i++) {
            if (kinds[i]) kindByHandle[uniq[i]] = kinds[i];
            if (kinds[i] === 'leadtime') cartHasLeadtime = true;
            if (kinds[i] === 'preorder') {
              var pre = getPreorderByHandle(uniq[i]);
              if (pre) cartPreorders.push(pre);
              else log('⚠️ kimchi-preorder 상품인데 날짜 없음(window.bcPreorder):', uniq[i], '→ 제한 보류');
            }
            if (kinds[i] === 'from') {
              var d = getDrop();
              if (d) cartFroms.push({ local: d.local, frozen: d.frozen, kind: 'from', handle: uniq[i] });
              else log('⚠️ preorder-from 상품인데 날짜 없음(window.limitedDrop):', uniq[i], '→ 제한 보류');
            }
          }
          ruleActive = cartHasLeadtime || cartPreorders.length > 0 || cartFroms.length > 0;
          log('재평가 — leadtime:', cartHasLeadtime, '| preorder:', cartPreorders.length, '| from:', cartFroms.length,
            '| zone:', getZone(), '| target:', getTargetStr(), '| fromMin:', getFromMin());
          scheduleApply();
          scheduleFlags();
          return ruleActive;
        });
      })
      .catch(function (e) { log('refresh 오류', e); return false; });
  }

  // ============================================================
  // 캘린더 적용. 리로드 없음.
  //   ①② → 목표일 1개만 남기고 숨김 + 목표일 선택
  //   ③   → 가장 빠른 날 이전 칩만 숨김 + 선택이 없거나 숨겨졌으면 가장 빠른 날 선택
  // ============================================================
  var observer = null;
  var applyTimer = null;
  var extendedCalendar = false;   // 캘린더 범위 확장은 1회만
  var lastClickedTarget = null;   // 마지막으로 클릭한 목표일(키)
  var clickCount = 0;             // 목표일당 클릭 횟수 (상한 5, 캘린더 다시 그리면 0으로)

  function scheduleApply() {
    if (applyTimer) clearTimeout(applyTimer);
    applyTimer = setTimeout(doApply, 80);
  }

  function clickOnce(el, key) {
    if (lastClickedTarget !== key) { lastClickedTarget = key; clickCount = 0; }
    if (clickCount < 5) {
      clickCount++;
      try { el.click(); log('날짜 선택 클릭:', key, '(' + clickCount + ')'); } catch (e) { /* noop */ }
    }
  }

  function extendCalendarFor(dateStr) {
    var td = parseYmd(dateStr);
    var days = td ? Math.round((td - laNow()) / 86400000) + 3 : 0;
    if (!extendedCalendar && days > 21 && typeof window.updateDeliveryCalendar === 'function') {
      extendedCalendar = true;
      log('날짜', dateStr, '이 캘린더 범위 밖 → 캘린더를', days, '일로 다시 그림');
      reconnectObserver();
      try { window.updateDeliveryCalendar(getZipForCalendar(), days); } catch (e) { log('캘린더 확장 실패', e); }
      return true;
    }
    return false;
  }

  function doApply() {
    var options = document.querySelectorAll('.date-option');
    if (!options.length) return;

    if (observer) observer.disconnect();

    var target = ruleActive ? getTargetStr() : null;
    var fromMin = (ruleActive && !target) ? getFromMin() : null;

    if (target) {
      rehideLiquid();
      // 목표일이 캘린더에 실제 있는지 확인
      var targetEl = null;
      for (var k = 0; k < options.length; k++) {
        if (options[k].dataset.date === target) { targetEl = options[k]; break; }
      }
      if (!targetEl) {
        restore();
        if (extendCalendarFor(target)) return;
        log('⚠️ 목표일', target, '이 캘린더에 없음 → 제한 보류(날짜 확인 필요)');
      } else {
        for (var i = 0; i < options.length; i++) {
          var o = options[i];
          var ds = o.dataset.date;
          if (!ds) continue;
          if (ds === target) {
            if (o.classList.contains('dlm-hidden')) o.classList.remove('dlm-hidden');
            if (o.style.display === 'none') o.style.display = '';
          } else {
            if (o.style.display !== 'none') o.style.display = 'none';
            o.classList.add('dlm-hidden');
            o.classList.remove('selected');
          }
        }
        // 목표일이 실제로 선택되도록 클릭 (숨은 폼필드 + _order_tags 갱신).
        // 캘린더가 렌더 100ms 뒤 첫 날짜를 자동 클릭해서 덮어쓰므로, 선택이 풀릴 때마다 다시 클릭 (상한 5회)
        if (!targetEl.classList.contains('selected')) clickOnce(targetEl, target);
        if (CONFIG.showNotice) updateNotice(lockNoticeText(target)); else removeNotice();
        log('적용 — 그날만:', target);
      }
    } else if (fromMin) {
      applyFrom(options, fromMin);
    } else if (ruleActive && cartFroms.length) {
      // 지난 사전예약(③만 있음): 제한 없음. Liquid 가 leadtime 태그를 보고 숨긴 칸은 다시 보이게
      restore();
      unhideLiquid();
    } else {
      restore();
    }

    reconnectObserver();
  }

  // ③ 사전예약: fromMin 이전 칩만 숨긴다
  function applyFrom(options, fromMin) {
    unhideLiquid();
    var firstOk = null, selected = null;
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      var ds = o.dataset.date;
      if (!ds) continue;
      if (ds < fromMin) {
        if (o.style.display !== 'none') o.style.display = 'none';
        o.classList.add('dlm-hidden');
        if (o.classList.contains('selected')) o.classList.remove('selected');
      } else {
        if (o.classList.contains('dlm-hidden')) o.classList.remove('dlm-hidden');
        if (o.style.display === 'none') o.style.display = '';
        if (!firstOk) firstOk = o;
        if (o.classList.contains('selected')) selected = o;
      }
    }
    if (!firstOk) {
      restore();
      if (extendCalendarFor(fromMin)) return;
      log('⚠️ 사전예약 시작일', fromMin, '이후 날짜가 캘린더에 없음 → 제한 보류');
      return;
    }
    if (!selected) clickOnce(firstOk, 'from:' + fromMin + ':' + firstOk.dataset.date);
    if (CONFIG.showNotice) updateNotice(fill(T().fromNotice, fromMin, namesFor('from'))); else removeNotice();
    log('적용 — 이날부터:', fromMin);
  }

  function restore() {
    var hidden = document.querySelectorAll('.date-option.dlm-hidden');
    for (var i = 0; i < hidden.length; i++) {
      hidden[i].classList.remove('dlm-hidden');
      if (hidden[i].style.display === 'none') hidden[i].style.display = '';
    }
    rehideLiquid();
    removeNotice();
  }

  // 카트 Liquid 가 leadtime 태그를 보고 숨긴 칸(제목·안내·날짜칩)을 ③ 에서는 다시 보이게
  function unhideLiquid() {
    var els = document.querySelectorAll('.bc-hide-when-limited');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.remove('bc-hide-when-limited');
      els[i].setAttribute('data-dlm-unhid', '1');
    }
    var note = document.getElementById('bc-limited-note'); // "날짜를 따로 고르지 않으셔도 돼요" 안내 → ③ 에선 맞지 않음
    if (note && note.getAttribute('data-dlm-hid') !== '1') {
      note.setAttribute('data-dlm-hid', '1');
      note.style.display = 'none';
    }
  }

  function rehideLiquid() {
    var els = document.querySelectorAll('[data-dlm-unhid="1"]');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add('bc-hide-when-limited');
      els[i].removeAttribute('data-dlm-unhid');
    }
    var note = document.getElementById('bc-limited-note');
    if (note && note.getAttribute('data-dlm-hid') === '1') {
      note.removeAttribute('data-dlm-hid');
      note.style.display = '';
    }
  }

  // ============================================================
  // 안내 문구 (주황 한 줄)
  // ============================================================
  function lockNoticeText(targetStr) {
    var active = getActiveDrop();
    var pre = active && active.kind === 'preorder';
    return fill(pre ? T().preNotice : T().lockNotice, targetStr, namesFor(pre ? 'preorder' : 'leadtime'));
  }

  function updateNotice(text) {
    var options = document.querySelectorAll('.date-option');
    if (!options.length) return;
    var container = options[0].parentNode;
    if (!container || !container.parentNode) return;
    var notice = document.getElementById('dlm-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'dlm-notice';
      notice.style.cssText =
        'margin:2px 0 4px;font-size:13px;line-height:1.4;color:#FF4713;font-weight:500;';
      container.parentNode.insertBefore(notice, container);
    }
    if (notice.textContent !== text) notice.textContent = text;
  }

  function removeNotice() {
    var n = document.getElementById('dlm-notice');
    if (n && n.parentNode) n.parentNode.removeChild(n);
  }

  // ============================================================
  // 상품 줄 표시 — 장바구니 페이지(tr.cart-item) · 미니카트(li[data-handle])
  // ============================================================
  var flagTimer = null;

  function scheduleFlags() {
    if (flagTimer) clearTimeout(flagTimer);
    flagTimer = setTimeout(applyFlags, 120);
  }

  function handleFromHref(href) {
    var m = /\/products\/([^\/\?#]+)/.exec(href || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function flagText(kind, handle) {
    var t = T();
    if (kind === 'leadtime') {
      var d = zoneDate(getDrop());
      return d ? fill(t.lockFlag, d) : t.lockFlagNoDate;
    }
    if (kind === 'preorder') {
      var p = zoneDate(getPreorderByHandle(handle));
      return p ? fill(t.preFlag, p) : t.preFlagNoDate;
    }
    if (kind === 'from') {
      var f = zoneDate(getDrop());
      if (!f || !fromActive(f)) return null; // 지난 사전예약은 표시 안 함
      return fill(t.fromFlag, f);
    }
    return null;
  }

  function upsertFlag(container, anchor, kind, handle) {
    var existing = container.querySelector('.dlm-item-flag');
    var text = kind ? flagText(kind, handle) : null;
    if (!text) {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }
    if (!existing) {
      existing = document.createElement('div');
      existing.className = 'dlm-item-flag';
      existing.setAttribute('data-dlm-kind', kind);
      existing.style.cssText =
        'display:inline-block;align-self:flex-start;max-width:100%;box-sizing:border-box;margin:6px 0 2px;padding:3px 10px;border-radius:999px;' +
        'background:#FFF1EA;color:#D63B0C;font-size:12px;line-height:16px;font-weight:700;letter-spacing:0;white-space:normal;';
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(existing, anchor.nextSibling);
      else container.appendChild(existing);
    }
    if (existing.getAttribute('data-dlm-kind') !== kind) existing.setAttribute('data-dlm-kind', kind);
    if (existing.textContent !== text) existing.textContent = text;
  }

  function applyFlags() {
    if (!CONFIG.showItemFlags) return;
    var i;
    // 1) 장바구니 페이지
    var rows = document.querySelectorAll('tr.cart-item');
    for (i = 0; i < rows.length; i++) {
      var row = rows[i];
      var link = row.querySelector('a.cart-item__name');
      var handle = handleFromHref(link && link.getAttribute('href'));
      var kind = kindFromTags((row.getAttribute('data-tags') || '').split(','));
      if (!kind && handle && kindByHandle[handle]) kind = kindByHandle[handle];
      upsertFlag(row, row.querySelector('.bc-name-row') || (link && link.parentNode), kind, handle);
    }
    // 2) 미니카트
    var lis = document.querySelectorAll('.mini-cart__navigation li[data-handle]');
    for (i = 0; i < lis.length; i++) {
      var li = lis[i];
      var h = li.getAttribute('data-handle') || '';
      var k = kindFromTags((li.getAttribute('data-tags') || '').split(','));
      upsertFlag(li, li.querySelector('.product-content'), k, h);
    }
  }

  // ============================================================
  // 판매 마감 — 상품 페이지에서 지정일 상품(①②)이고 마감시각 지났으면
  //            장바구니 담기 버튼 비활성 + 'closed' 표시
  //            ③ 은 마감 없음 → 안내만 '그날부터'로 바꿈
  // ============================================================
  function applyProductCutoff() {
    var handle = handleFromPath();
    if (!handle) return; // 상품 페이지 아님

    productKind(handle).then(function (kind) {
      if (!kind) { unblockProduct(); return; }
      if (kind === 'from') { unblockProduct(); applyFromProductNotice(); return; }
      var drop = null;
      if (kind === 'leadtime') drop = getDrop();
      if (kind === 'preorder') {
        var pp = window.bcPreorderProduct;
        if (pp && pp.local && (!pp.handle || pp.handle === handle)) drop = { local: pp.local, frozen: pp.frozen || '', kind: 'preorder' };
      }
      if (drop && isClosedFor(drop)) blockProduct(kind); else unblockProduct();
    });
  }

  // ③ 상품 페이지 안내 — pn365(템플릿 안내 박스)가 있으면 글만 바꾸고, 없으면 담기 버튼 위에 한 줄
  function applyFromProductNotice() {
    var drop = getDrop();
    var min = drop && drop.local;
    var line = document.getElementById('dlm-from-msg');
    if (!min || !fromActive(min)) {
      if (line && line.parentNode) line.parentNode.removeChild(line);
      return;
    }
    var t = T();
    var dateTxt = fill(t.pnDate, min);
    if (drop.frozen && drop.frozen !== min) dateTxt += ' · ' + (lang() === 'ko' ? '냉동 ' : 'Frozen ') + dateLabel(drop.frozen);
    var nodes = document.querySelectorAll('.pn365');
    if (nodes.length) {
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var tt = n.querySelector('.pn365-t'), dd = n.querySelector('.pn365-d'), ss = n.querySelector('.pn365-s');
        if (tt && tt.textContent !== t.pnTitle) tt.textContent = t.pnTitle;
        if (dd && dd.textContent !== dateTxt) dd.textContent = dateTxt;
        if (ss && ss.textContent !== t.pnSub) ss.textContent = t.pnSub;
        n.removeAttribute('data-pending');
        n.setAttribute('data-dlm-from', '1');
        if (n.style.display !== 'flex') n.style.display = 'flex';
      }
      if (line && line.parentNode) line.parentNode.removeChild(line);
      return;
    }
    var btns = addToCartButtons();
    if (!btns.length) return;
    var txt = fill(t.fromLine, min);
    if (!line) {
      line = document.createElement('div');
      line.id = 'dlm-from-msg';
      line.style.cssText = 'margin:8px 0;font-size:13px;color:#D63B0C;font-weight:600;';
      var ref = btns[0].closest('form') || btns[0];
      ref.parentNode.insertBefore(line, ref);
    }
    if (line.textContent !== txt) line.textContent = txt;
  }

  function addToCartButtons() {
    return document.querySelectorAll(
      'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button[name="add"], ' +
      '.product-form__submit, button.product-form__submit, [data-add-to-cart]'
    );
  }

  function blockProduct(kind) {
    var btns = addToCartButtons();
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.dataset.dlmBlocked) continue;
      b.dataset.dlmBlocked = '1';
      b.dataset.dlmText = b.innerHTML;
      b.disabled = true;
      b.style.opacity = '0.5';
      b.style.cursor = 'not-allowed';
      b.innerHTML = 'Sold out';
    }
    // 마감 안내 한 줄
    if (btns.length && !document.getElementById('dlm-closed-msg')) {
      var msg = document.createElement('div');
      msg.id = 'dlm-closed-msg';
      msg.textContent = (kind === 'preorder') ? CONFIG.preorderClosedText : CONFIG.closedText;
      msg.style.cssText = 'margin:8px 0;font-size:13px;color:#FF4713;font-weight:500;';
      var ref = btns[0].closest('form') || btns[0];
      ref.parentNode.insertBefore(msg, ref);
    }
    log('🚫 판매 마감 — 담기 버튼 비활성:', handleFromPath(), kind);
  }

  function unblockProduct() {
    var btns = document.querySelectorAll('[data-dlm-blocked="1"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      b.disabled = false;
      b.style.opacity = '';
      b.style.cursor = '';
      if (b.dataset.dlmText) b.innerHTML = b.dataset.dlmText;
      delete b.dataset.dlmBlocked;
      delete b.dataset.dlmText;
    }
    var msg = document.getElementById('dlm-closed-msg');
    if (msg && msg.parentNode) msg.parentNode.removeChild(msg);
  }

  function handleFromPath() {
    var m = /\/products\/([^\/\?#]+)/.exec(location.pathname);
    return m ? m[1] : '';
  }

  // ============================================================
  // 트리거
  // ============================================================
  function reconnectObserver() {
    var root = document.querySelector('.date-option');
    var target = (root && root.parentNode && root.parentNode.parentNode) || document.body;
    if (!observer) observer = new MutationObserver(function () { scheduleApply(); });
    observer.observe(target, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
  }

  function bind() {
    function onCalendar() { clickCount = 0; setTimeout(refresh, 350); }
    document.addEventListener('calendarRendered', onCalendar);
    window.addEventListener('calendarRendered', onCalendar);
    window.addEventListener('chefBanchanZipCodeChanged', function () { lastFingerprint = null; clickCount = 0; setTimeout(refresh, 300); });

    if (window.fetch && !window.__dlmFetchPatched) {
      window.__dlmFetchPatched = true;
      var of = window.fetch;
      window.fetch = function () {
        var p = of.apply(this, arguments);
        try {
          var url = arguments[0];
          var u = typeof url === 'string' ? url : (url && url.url) || '';
          if (/\/cart\/(add|change|update|clear)/i.test(u)) p.then(function () { setTimeout(refresh, 250); setTimeout(applyFlags, 1200); });
        } catch (e) { /* noop */ }
        return p;
      };
    }

    // 상품 줄 표시는 장바구니/미니카트가 다시 그려질 때마다 (자기 변경은 같은 글이면 건드리지 않아 반복 안 됨)
    if (CONFIG.showItemFlags && window.MutationObserver) {
      new MutationObserver(function () { scheduleFlags(); }).observe(document.body, { childList: true, subtree: true });
    }

    reconnectObserver();
    setTimeout(refresh, 400);
    scheduleFlags();
    applyProductCutoff();
    setTimeout(applyProductCutoff, 900);  // 템플릿 안내(pn365)가 600ms에 채워진 뒤 한 번 더
    setTimeout(applyProductCutoff, 2500);
    // 자정 넘어 마감시각 지나는 경우 대비 — 5분마다 마감 재확인(상품 페이지)
    setInterval(applyProductCutoff, 5 * 60 * 1000);
  }

  // ============================================================
  // 공개 API (콘솔 테스트용)
  // ============================================================
  window.DeliveryLeadtimeManager = {
    version: '3.1',
    config: CONFIG,
    refresh: refresh,
    getDrop: getDrop,
    getActiveDrop: getActiveDrop,
    getZone: getZone,
    getTargetStr: getTargetStr,
    getFromMin: getFromMin,
    isClosed: isClosed,
    productHasTag: productHasTag,
    productKind: productKind,
    applyFlags: applyFlags,
    _state: function () {
      return {
        ruleActive: ruleActive, zone: getZone(), leadtime: cartHasLeadtime, preorders: cartPreorders, froms: cartFroms,
        kinds: kindByHandle, drop: getDrop(), active: getActiveDrop(), target: getTargetStr(), fromMin: getFromMin(), closed: isClosed()
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  log('✅ V3.1 Ready — leadtime / kimchi-preorder (그날만) + preorder-from (그날부터) | 상품 줄 표시');
})();
